import React from 'react';

import { InformationDataType } from '@components/widgets/information-box';
import InformationBox from '@components/widgets/information-box/InformationBox';
import { MAIN_THEME } from '@styles';
import { valueCommaSeparated } from '@utils/formatting-utils';
import { getGenericLine, getLineCategory } from '@utils/helper-utils';
import { GenericLines, LineCategory } from 'src/enums';

import {
  AmendmentDetails,
  AmendmentOfferDetails
} from '../types/getAmendmentsDetailsResponse.types';

import DateService from 'src/core/services/DateService';
import FormattingService from 'src/core/services/FormattingService';
import { makeStyles } from 'tss-react/mui';

type Props = { data: AmendmentDetails; lineCode?: string };

const useStyles = makeStyles()(() => ({
  container: { width: '100%' },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'space-between'
  },
  title: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '17px',
    display: 'block',
    textAlign: 'left'
  },
  detailContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    flexWrap: 'wrap',
    paddingTop: '10px'
  },
  mainAmountValue: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.lg,
    lineHeight: '20px',
    float: 'left',
    fontWeight: 'bold'
  },
  premiumAmount: {
    color: MAIN_THEME.color.red,
    fontSize: MAIN_THEME.fontSize.lg,
    lineHeight: '20px',
    fontWeight: 'bold',
    float: 'right'
  }
}));

const n = (v: any): number => {
  const num = Number(v ?? 0);

  return Number.isFinite(num) ? num : 0;
};

const getSym = (...xs: Array<string | undefined>): string =>
  xs.find((s) => !!s)?.toString() ?? '';

const buildBreakdownData = (
  src?: AmendmentOfferDetails | null,
  currencySymbol?: string
) => {
  if (!src) return {};

  return {
    BasicPremium: src.basicPremium ?? 0,
    NetPremium: src.netPremium ?? 0,
    PolicyCostAmount: src.policyCost ?? 0,
    Charges: src.chargesAmount ?? src.charges ?? 0,
    TPAFees: src.tpaFeesTotalAmount ?? src.tpaFees ?? 0,
    GrossPremium: src.grossPremium ?? 0,
    FixedStamp: src.fixedStampAmount ?? src.fixedStamp ?? 0,
    ProportionalStamp:
      src.proportionalStampAmount ?? src.proportionalStamp ?? 0,
    MunicipalityTax: src.municipalityTaxAmount ?? src.municipalityTax ?? 0,
    TotalPremium: src.totalPremium ?? 0,
    __currency: currencySymbol || src?.currency?.symbol || ''
  };
};

const headers = (currencySymbol: string) => ({
  BasicPremium: {
    name: 'BasicPremium',
    title: 'Basic Premium',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  NetPremium: {
    name: 'NetPremium',
    title: 'Net Premium',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  PolicyCostAmount: {
    name: 'PolicyCostAmount',
    title: 'Policy Cost',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  Charges: {
    name: 'Charges',
    title: 'Charges',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  TPAFees: {
    name: 'TPAFees',
    title: 'TPA Fees',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  GrossPremium: {
    name: 'GrossPremium',
    title: 'Gross Premium',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  FixedStamp: {
    name: 'FixedStamp',
    title: 'Fixed Stamp',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  ProportionalStamp: {
    name: 'ProportionalStamp',
    title: 'Proportional Stamp',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  MunicipalityTax: {
    name: 'MunicipalityTax',
    title: 'Municipality Tax',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  TotalPremium: {
    name: 'TotalPremium',
    title: 'Total Premium',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  }
});

const OfferDetailsCancellationWidgetAtom: React.FC<Props> = ({ data, lineCode }) => {
  const { classes } = useStyles();

  const currentPQ = data?.currentOfferDetails;
  const variance = data?.policyQuoteVariance;
  const newPQ = data?.offerDetails;

  const policySym =
    (data as any)?.policyDetails?.currency?.symbol ||
    (data as any)?.summaryDetails?.currency?.symbol ||
    '';

  const symCur = getSym(currentPQ?.currency?.symbol, policySym);
  const symVar = getSym(variance?.currency?.symbol, policySym);
  const symNew = getSym(newPQ?.currency?.symbol, policySym);
  const headerCurrencySymbol = getSym(symNew, policySym, symCur, symVar);

  const headerCreatedOn = newPQ?.createdOn
    ? DateService.formatDate(newPQ.createdOn)
    : '';
  const headerStatus = FormattingService.formatEnumToDisplay(
    newPQ?.status ?? ''
  );
  const headerSumInsured = `${symNew} ${valueCommaSeparated(
    n(newPQ?.sumInsured).toFixed(0)
  )}`;
  const isGeneric = getLineCategory(lineCode ?? '') === LineCategory.Generic;
  const headerTotalPremium = isGeneric
    ? `${symVar} ${valueCommaSeparated(n(variance?.totalPremium).toFixed(0))}`
    : `${symNew} ${valueCommaSeparated(n(newPQ?.totalPremium).toFixed(0))}`;

  const currentBreakdown = buildBreakdownData(currentPQ, symCur);
  const varianceBreakdown = buildBreakdownData(variance, symVar);

  // Conditionally build comparison data and grouping values
  const comparisonData = {
    Current: currentBreakdown,
    Variance: varianceBreakdown
  };

  const groupingValues = ['Current', 'Variance'];

  const resolvedLineCode =
    lineCode ?? String(data?.summaryDetails?.lineCode ?? '');
  const isGroupMedical =
    getGenericLine(resolvedLineCode) === GenericLines.MEDICAL_GROUP;
  const widgetTitle = isGroupMedical ? 'Group Offer Details' : 'Offer Details';

  return (
    <InformationBox
      visibleRows={0}
      title={widgetTitle}
      status={headerStatus}
      statusReason=""
      statusIcon=""
      statusColor=""
      reviewedOn=""
      subtitle={`Created On: ${headerCreatedOn}`}
      header={
        <div className={classes.container}>
          <div className={classes.titleContainer}>
            <span className={classes.title}>Coverage</span>
            <span className={classes.title}>Total Premium</span>
          </div>
          <div className={classes.detailContainer}>
            <span className={classes.mainAmountValue}>{headerSumInsured}</span>
            <span className={classes.premiumAmount}>{headerTotalPremium}</span>
          </div>
        </div>
      }
      tabs={
        [
          {
            title: 'Comparison',
            name: 'Comparison',
            data: comparisonData,
            headers: headers(headerCurrencySymbol),
            supportsGrouping: true,
            groupingValues: groupingValues,
            groupingTitle: null,
            supportCycleDivision: false
          }
        ] as any[]
      }
      collapsible={false}
      initiallyCollapsed={false}
    />
  );
};

export { OfferDetailsCancellationWidgetAtom };
